<template>
	<div class="cassie-vertical-md">
		<SectionCard>
			<template #title>
				<slot name="title" />
			</template>
			<template #body>
				<div class="cassie-vertical-md">
					<div class="d-flex cassie-horizontal-md">
						<TextField
							:value="user.forename"
							:disabled="isScimManaged"
							:view-only="!userCanCreateUpdate && !isScimManaged"
							:rules="{required: true, max: 60}"
							label="Forename *"
							class="cassie-input-width-md"
							@input="updateUser('forename', $event)"
						/>
						<TextField
							:value="user.surname"
							:disabled="isScimManaged"
							:view-only="!userCanCreateUpdate && !isScimManaged"
							:rules="{required: true, max: 60}"
							label="Surname *"
							class="cassie-input-width-md"
							@input="updateUser('surname', $event)"
						/>
					</div>
					<div class="d-flex cassie-horizontal-md">
						<TextField
							:value="user.emailAddress"
							:disabled="isScimManaged"
							:view-only="!userCanCreateUpdate && !isScimManaged"
							:rules="{ required: true, email: true, max: 128 }"
							label="Email Address *"
							class="cassie-input-width-md"
							@input="updateUser('emailAddress', $event)"
						/>
						<TextField
							:value="user.telephoneNo"
							:disabled="isScimManaged"
							:view-only="!userCanCreateUpdate && !isScimManaged"
							label="Telephone No."
							class="cassie-input-width-md"
							rules="phoneNumber"
							@input="updateUser('telephoneNo', $event)"
						/>
					</div>
					<div class="d-flex cassie-horizontal-md">
						<TextField
							:value="user.mobileNo"
							:disabled="isScimManaged"
							:view-only="!userCanCreateUpdate && !isScimManaged"
							label="Mobile No."
							class="cassie-input-width-md"
							rules="phoneNumber"
							@input="updateUser('mobileNo', $event)"
						/>
						<Dropdown
							:value="userDefaultDashboard"
							:view-only="!userCanCreateUpdate"
							:items="dashboardOptions"
							:label="'Default Dashboard *' | useLabels"
							rules="required"
							class="cassie-input-width-md"
							@input="updateUser('defaultDashboard', $event)"
						/>
					</div>
					<div class="d-flex cassie-horizontal-md">
						<Dropdown
							v-model="userBrandIds"
							:search-input.sync="brandSearchInput"
							:items="brandOptionsWithDisabledState"
							:disabled="isScimManaged || !userCanCreateUpdate || brandOptionsWithDisabledState.length === 1"
							custom-sort
							:label="brandLabel"
							searchable
							multiple
							:rules="{ requiredBrands: user.brandIds.length === 0 }"
							:explainer="userHasUniversalBrand && brandOptions.length > 1 ? 'Universal brand cannot be combined with specific brands' : undefined"
							class="cassie-input-width-md"
						/>
					</div>
					<div class="d-flex cassie-horizontal-md">
						<Toggle
							:value="user.isActive"
							:disabled="isScimManaged || !userCanCreateUpdate"
							label="User Active"
							class="cassie-input-width-md"
							@update:value="updateUser('isActive', $event)"
						/>
					</div>
					<PrimaryActionButton
						v-if="user.userId && userCanCreateUpdate && ssoBypassAllowed"
						@click="resetPassword"
					>
						Reset Password
					</PrimaryActionButton>
				</div>
				<PasswordResetModal
					v-if="showPasswordResetModal"
					@close="showPasswordResetModal = false"
				/>
			</template>
		</SectionCard>
		<SectionCard>
			<template #title>
				User Role
			</template>
			<template #body>
				<div class="cassie-vertical-md">
					<div class="d-flex flex-row cassie-horizontal-md align-center">
						<Dropdown
							v-model="selectedUserRole"
							label="User Roles"
							:items="userRoleItems"
							searchable
							return-object
							item-disabled="disabled"
							class="cassie-input-width-md"
							:rules="userCanCreateUpdate ? 'required' : ''"
							:disabled="isScimManaged"
							:view-only="!userCanCreateUpdate && !isScimManaged"
							@change="updatedUserRole"
						/>
						<Toggle
							:value="showPermissions"
							:disabled="!selectedUserRole"
							label="Show Permissions"
							class="cassie-input-width-md"
							@update:value="updateShowPermissions($event)"
						/>
					</div>
					<DataTable
						v-if="selectedUserRole && showPermissions"
						:items="tableData"
						:headers="tableHeaders"
					/>
				</div>
			</template>
		</SectionCard>
		<SectionCard v-if="showLegacyPermissions">
			<template #title>
				Legacy Permission Groups
			</template>
			<template #subtitle>
				{{ userCanCreateUpdate ? 'Manage' : '' }} Permission Groups used by V1 Admin Portal
			</template>
			<template #body>
				<div class="cassie-vertical-md">
					<div class="d-flex flex-row cassie-horizontal-md">
						<Dropdown
							v-model="userLegacyPermissionGroups"
							:search-input.sync="searchInput"
							label="Permission Groups"
							:items="availableLegacyPermissionGroupsOptions"
							return-object
							multiple
							searchable
							class="cassie-input-width-md"
							:disabled="isScimManaged || !userCanCreateUpdate"
						/>
					</div>
				</div>
			</template>
		</SectionCard>
	</div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import Toggle from '../../../../../../shared/components/toggle.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import {
	adminPortalProductRoles, legacyPermissionGroups, adminPortalSsoEnabled, adminPortalSsoBypassDomains, adminPortalPermissions
} from '../../../../../../shared/state/admin-portal-users-and-lookup-data.js'
import { brandOptions } from '../../../../../../shared/state/brands.js'
import { sendResetPasswordEmailApi } from '../../../../../../shared/utils/api/admin-portal-users.js'
import { adminPortalBrandFieldLabel, showLegacyPermissions, submissionFragmentsEnabled, prefPageAnalyticsEnabled } from '../../../../../../shared/state/config-keys.js'
import { CAN_SEE_CONSENT_BANNERS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import PasswordResetModal from '../../../../../../shared/components/password-reset-modal.vue'
export default {
	components: {
		PasswordResetModal,
		SectionCard,
		TextField,
		Dropdown,
		Toggle,
		PrimaryActionButton,
		DataTable
	},
	props: {
		user: {
			type: Object,
			default: () => ({})
		},
		resetValidation: {
			type: Function,
			default: () => {}
		},
		userCanCreateUpdate: Boolean,
		ssoEnabled: Boolean,
		isScimManaged: Boolean
	},
	setup () {
		return {
			brandOptions,
			adminPortalProductRoles,
			adminPortalBrandFieldLabel,
			legacyPermissionGroups,
			showLegacyPermissions,
			adminPortalSsoEnabled,
			adminPortalSsoBypassDomains,
			adminPortalPermissions
		}
	},
	data () {
		return {
			selectedUserRole: null,
			showPasswordResetModal: false,
			originalEmail: null,
			showPermissions: false,
			searchInput: null,
			brandSearchInput: null
		}
	},
	computed: {
		...mapGetters('dashboards', ['dashboards']),
		...mapGetters('auth', ['productAreaPermission', 'userDefaultDashboardId']),
		tableData () {
			const data = this.selectedUserRole && this.adminPortalProductRoles.find(role => role.productRoleId === this.selectedUserRole.value).productRolePermissions
			return data
		},
		userRoleItems () {
			const roleItems = adminPortalProductRoles.value.map(role => (
				{
					value: role.productRoleId,
					text: role.productRoleName
				}
			))
			return roleItems
		},
		userLegacyPermissionGroups: {
			get () {
				if (!this.user.legacyPermissionsData || !legacyPermissionGroups.value) return []
				return this.user.legacyPermissionsData.map(datum => {
					const group = legacyPermissionGroups.value.find(({ permissionGroupId }) => permissionGroupId === datum.permissionGroupId)
					return {
						...datum,
						value: datum.permissionGroupId,
						text: group?.permissionGroupName || datum.permissionGroupId
					}
				})
			},
			set (value) {
				const legacyPermissionsData = value.map(item => ({
					permissionGroupId: item.permissionGroupId || item.value
				}))

				// Only update if the value has actually changed (use Set for order-independent comparison)
				const currentData = this.user.legacyPermissionsData || []
				if (currentData.length !== legacyPermissionsData.length) {
					this.updateUser('legacyPermissionsData', legacyPermissionsData)
					return
				}

				// Compare as sets (order-independent)
				const currentIds = new Set(currentData.map(d => d.permissionGroupId))
				const newIds = new Set(legacyPermissionsData.map(d => d.permissionGroupId))
				const hasChanged = currentData.some(d => !newIds.has(d.permissionGroupId)) ||
					legacyPermissionsData.some(d => !currentIds.has(d.permissionGroupId))

				if (hasChanged) {
					this.updateUser('legacyPermissionsData', legacyPermissionsData)
				}
			}
		},
		availableLegacyPermissionGroupsOptions () {
			if (!legacyPermissionGroups.value) return []
			return legacyPermissionGroups.value.map(permissionGroup => ({
				...permissionGroup,
				value: permissionGroup.permissionGroupId,
				text: permissionGroup.permissionGroupName
			}))
		},
		userBrandIds: {
			get () {
				return this.user.brandIds || []
			},
			set (value) {
				// Only update if the value has actually changed (use Set for order-independent comparison)
				const currentIds = this.user.brandIds || []
				if (currentIds.length !== value.length) {
					this.updateUser('brandIds', value)
					return
				}

				// Compare as sets (order-independent)
				const currentSet = new Set(currentIds)
				const valueSet = new Set(value)
				const hasChanged = currentIds.some(id => !valueSet.has(id)) || value.some(id => !currentSet.has(id))

				if (hasChanged) {
					this.updateUser('brandIds', value)
				}
			}
		},
		userHasUniversalBrand () {
			return this.user.brandIds?.includes(0)
		},
		brandOptionsWithDisabledState () {
			if (!brandOptions.value) return []
			const hasUniversal = this.user.brandIds?.includes(0)
			const hasSpecificBrands = this.user.brandIds?.some(id => id !== 0)

			return brandOptions.value.map(brand => {
				// If Universal is selected, disable all other brands
				if (hasUniversal && brand.value !== 0) {
					return {
						...brand,
						disabled: true
					}
				}
				// If specific brands selected, disable Universal
				if (hasSpecificBrands && brand.value === 0) {
					return {
						...brand,
						disabled: true
					}
				}
				return brand
			})
		},
		singleBrandOptionExists () {
			return this.brandOptions.length === 1
		},
		brandLabel () {
			// Apply custom terminology and handle singular/plural based on available options
			const baseLabel = this.brandOptionsWithDisabledState.length === 1 ? 'Brand' : 'Brands'
			const label = this.$options.filters.useLabels(baseLabel)
			return this.brandOptionsWithDisabledState.length === 1 ? label : `${label} *`
		},
		dashboardOptions () {
			return this.dashboards.map(({ id, name }) => ({
				value: id,
				text: name
			}))
		},
		tableHeaders () {
			const headers =
				[
					{
						text: 'Module',
						value: 'module',
						width: '30%'
					},
					{
						text: 'Page',
						value: 'page',
						width: '30%'
					},
					{
						text: 'Access Permission',
						value: 'accessPermission',
						width: '40%'
					}
				]

			return headers
		},
		ssoBypassAllowed () {
			if (!this.originalEmail) return false
			if (this.originalEmail.toLowerCase() !== this.user.emailAddress.toLowerCase()) {
				return false
			}
			if (adminPortalSsoEnabled.value) {
				return adminPortalSsoBypassDomains.value?.find(m => this.user.emailAddress.toLowerCase().endsWith(m.domain))
			} else return true
		},
		userDefaultDashboard () {
			if (this.user.defaultDashboard === 0 && submissionFragmentsEnabled.value) {
				return 4 // Associated Cookie Submissions
			} else if ((this.user.defaultDashboard === 4 || this.user.defaultDashboard === 5) && !submissionFragmentsEnabled.value) {
				return 0
			} else {
				return this.user.defaultDashboard
			}
		}
	},
	async created () {
		await this.getDashboards({
			addCookieModule: this.productAreaPermission(CAN_SEE_CONSENT_BANNERS),
			addPrefPageAnalytics: prefPageAnalyticsEnabled.value,
			submissionFragmentsEnabled: false
		})
		this.originalEmail = this.user.emailAddress

		// Auto-select brand when only one option exists (e.g. Universal only).
		// Use a watcher so this still works when brandOptions are loaded asynchronously.
		this.$watch(
			() => brandOptions.value,
			newBrandOptions => {
				if (
					(!this.user.brandIds || this.user.brandIds.length === 0) &&
					newBrandOptions?.length === 1
				) {
					this.updateUser('brandIds', [newBrandOptions[0].value])
				}
			},
			{ immediate: true }
		)

		const selectedUserRole = this.adminPortalProductRoles.find(({ productRoleId }) => productRoleId === this.user.userRoleId)
		if (selectedUserRole) {
			this.selectedUserRole = {
				value: selectedUserRole.productRoleId,
				text: selectedUserRole.productRoleName
			}
		}

		// Reset validation after data is loaded and dropdowns have initialized
		// Wait for next tick to ensure all bindings are complete
		await this.$nextTick()
		setTimeout(() => {
			if (this.resetValidation) {
				this.resetValidation()
			}
		}, 200)
	},
	methods: {
		...mapActions('dashboards', ['getDashboards']),
		async resetPassword () {
			await sendResetPasswordEmailApi({
				emailAddress: this.user.emailAddress
			})
			this.showPasswordResetModal = true
		},
		async updateUser (property, value) {
			await this.$emit('update:user', {
				...this.user,
				[property]: value
			})
		},
		updatedUserRole () {
			this.updateUser('userRoleId', this.selectedUserRole.value)
			if (!this.showPermissions) this.updateShowPermissions(true)
		},
		updateShowPermissions (value) {
			this.showPermissions = value
		}
	}
}
</script>

<style>
/* Make disabled brand options visually distinct */
.cassie-dropdown .v-list-item--disabled {
	opacity: 0.5;
	color: var(--global-muted-foreground, #9e9e9e) !important;
	pointer-events: none;
}

.cassie-dropdown .v-list-item--disabled .v-list-item__title {
	color: var(--global-muted-foreground, #9e9e9e) !important;
	text-decoration: line-through;
}

.cassie-dropdown .v-list-item--disabled .v-simple-checkbox {
	opacity: 0.5;
}
</style>
